import { XCircleIcon } from '@heroicons/react/outline';
import { orderBy, uniq } from 'lodash';
import { useMemo } from 'react';
import { useAppDispatch, useAppSelector } from 'store';
import {
  composableFiltersState,
  MainOperatorEnum,
  removeFilterClause,
  selectMainOperator,
  SingleFilterClause,
  updateFilterClause,
} from 'store/composableFiltersSlice';
import { OperatorField } from '../OperatorField';
import { PropertyField } from '../PropertyField';
import { ValueField } from '../ValueField';
import { ClausePrefix } from './ClausePrefix';

interface FilterClauseProps {
  data: SingleFilterClause;
  index: number;
  noPrefix?: boolean;
}

export const FilterClause = ({ data, index, noPrefix }: FilterClauseProps) => {
  const { data: rawData } = useAppSelector(composableFiltersState);

  const dispatch = useAppDispatch();

  const selectOptions = useMemo(() => {
    const selectedProperty = rawData.filters?.find(
      (item) => item.name === data?.property?.name
    );

    const propertyGroups = orderBy(
      uniq(rawData.filters?.map((property) => property.group))
    );

    const properties = propertyGroups.map((group) => {
      const filteredProperties = rawData.filters?.filter(
        (item) => item.group === group
      );

      return {
        label: group,
        options: filteredProperties?.map((item) => ({
          value: item.name,
          label: item.display,
        })),
      };
    });

    const operators = selectedProperty?.operators?.map((option) => ({
      value: option.name,
      label: option.display.toLowerCase(),
    }));

    const values = orderBy(
      selectedProperty?.values?.map((option) => ({
        value: option.name,
        label: option.display,
      })),
      ['label']
    );

    return {
      properties,
      operators,
      values,
      type: selectedProperty?.type,
    };
  }, [data.property, rawData.filters]);

  const handleSelectMainOperator = (selectedOption: {
    value: string;
    label: string;
  }) => {
    dispatch(
      selectMainOperator({
        type: 'singleFilterClause',
        value: selectedOption?.value as MainOperatorEnum,
      })
    );
  };

  const handleUpdateValueField = (e: any) => {
    dispatch(
      updateFilterClause({
        index,
        type: 'singleFilterClause',
        data: {
          value: e,
        },
      })
    );
  };

  const handleUpdateOperatorField = (selectedOption: {
    value: string;
    label: string;
  }) => {
    dispatch(
      updateFilterClause({
        index,
        type: 'singleFilterClause',
        data: {
          operator: {
            name: String(selectedOption?.value),
            display: String(selectedOption?.label),
          },
        },
      })
    );
  };

  const handleUpdatePropertyField = (selectedOption: {
    value: string;
    label: string;
  }) => {
    dispatch(
      updateFilterClause({
        index,
        type: 'singleFilterClause',
        data: {
          property: {
            name: String(selectedOption?.value),
            display: String(selectedOption?.label),
          },
          operator: {
            name: '',
            display: '',
          },
          value: '',
        },
      })
    );
  };

  const handleDelete = () => {
    dispatch(removeFilterClause({ index, type: 'singleFilterClause' }));
  };

  return (
    <div className="flex items-start pl-3 space-x-2">
      <div className="flex items-center space-x-2">
        {noPrefix ? null : (
          <div className="w-12">
            <ClausePrefix
              index={index}
              handleSelectOperator={handleSelectMainOperator}
            />
          </div>
        )}
        <PropertyField
          selectOptions={selectOptions}
          filterState={data}
          handlePropertyFieldUpdate={handleUpdatePropertyField}
          placement="single"
        />
      </div>
      <OperatorField
        selectOptions={selectOptions}
        filterState={data}
        handleUpdateOperatorField={handleUpdateOperatorField}
        placement="single"
      />
      <ValueField
        selectOptions={selectOptions}
        filterState={data}
        handleUpdateValueField={handleUpdateValueField}
        placement="single"
      />
      <button className="place-self-center" onClick={handleDelete}>
        <XCircleIcon className="w-5 h-5 text-gray-500" />
      </button>
    </div>
  );
};
